/**
 * The clock a daily board is timed by.
 *
 * It runs in the browser rather than on the server because what it measures is
 * a thing only the browser can see: time spent with the day actually in front
 * of you. The server can only ever measure the gap between two guesses, which
 * counts the school run, the meeting and the night's sleep as solving.
 *
 * So it starts when the board opens, stops the moment the page goes away -
 * another tab, another app, a locked phone, a closed window - and picks up
 * where it left off when the player comes back. What it counts is attention,
 * which is what the day was asking for.
 *
 * A player who wants to hand in a number they did not earn can. That is not
 * an oversight: this is a code to swap with friends over breakfast, not a
 * record anybody is defending.
 */

import { persist, readJSON } from './storage.js';

/**
 * The one key a clock is kept under, holding the day it belongs to and the
 * milliseconds counted so far.
 *
 * One key holding one day, rather than a list like the drafts: there is only
 * ever one day being played, so opening today drops yesterday rather than
 * accumulating a record of which mornings this browser showed up for.
 */
export const CLOCK_KEY = 'unkoad.clock';

/**
 * A clock for one board.
 *
 * It is created stopped. The page decides when it is running, because the page
 * is the thing that knows whether it is being looked at - a board that started
 * itself would count the seconds of a tab restored into the background.
 */
export function createClock({ storage = null, key = '', now = () => Date.now() } = {}) {
    // What has been banked by earlier visits and earlier runs. Read once: this
    // is the only thing that writes here, so nothing else can move it.
    let counted = key ? stored(storage, key) : 0;

    // When the current run began, or null when the clock is not running. Null
    // rather than zero, for the reason createReviver gives: a clock is allowed
    // to read zero.
    let since = null;

    /** Banks the run in progress, if there is one. */
    const bank = () => {
        if (since === null) return;
        // Guarded, because Date.now() can go backwards over a clock
        // adjustment, and a leap back would otherwise refund time.
        const run = now() - since;
        if (run > 0) counted += run;
        since = null;
    };

    return {
        /** Milliseconds counted, run in progress included. */
        elapsed() {
            if (since === null) return counted;
            return counted + Math.max(0, now() - since);
        },

        /** Whether the clock is currently counting. */
        get running() {
            return since !== null;
        },

        /** Starts counting, or carries on if it already is. */
        run() {
            if (since === null) since = now();
        },

        /**
         * Stops counting and puts the total away.
         *
         * Written here rather than on a timer because this is every way the
         * page can be left: a tab switch, an app switch, a close. A page killed
         * without warning loses the run in progress, which loses the player
         * time rather than giving them any.
         */
        hold() {
            bank();
            this.save();
        },

        /** Puts the total away without stopping. Called at each guess. */
        save() {
            if (!key) return;
            persist(storage, CLOCK_KEY, JSON.stringify({ key, ms: this.elapsed() }));
        },

        /**
         * The day is over: stop, and forget it. The duration has gone to the
         * server with the row that ended the attempt, so what is here is no
         * longer working state - it is a record of a morning, which is the one
         * thing this must not become.
         */
        stop() {
            bank();
            this.clear();
        },

        clear() {
            if (!key) return;
            persist(storage, CLOCK_KEY, JSON.stringify({ key: '', ms: 0 }));
        },
    };
}

/**
 * What this board has already counted, or zero for anything else: a clock left
 * by another day, by a version that wrote something else here, or by a browser
 * that will not let it be read at all.
 */
function stored(storage, key) {
    const parsed = readJSON(storage, CLOCK_KEY);
    if (!parsed || typeof parsed !== 'object') return 0;
    if (parsed.key !== key) return 0;
    if (typeof parsed.ms !== 'number' || !Number.isFinite(parsed.ms) || parsed.ms < 0) return 0;
    return parsed.ms;
}
